import { z } from "zod";
import { uuidSchema } from "./ids";
import { systemRoleSchema } from "./rbac";

// =============================================================================
// API-Vertrag der Plattform-Endpunkte (Entscheidung 36)
// =============================================================================
// Request- und Response-Schemas stehen EINMAL hier. Die API (Hono) validiert
// eingehende Requests damit, die Web-App (und später die Mobile-App) prüft die
// Antworten damit. `z.infer<typeof schema>` leitet daraus den TypeScript-Typ ab –
// Schema und Typ können so nie auseinanderlaufen.
//
// Nur Formate, keine Sicherheitsentscheidungen: Wer was darf, prüft die API
// (requireTenant, authorize) und die DB (RLS, SECURITY-DEFINER-Funktionen).

// --- Bausteine ---------------------------------------------------------------

// Subdomain eines Mandanten: DNS-Label, 3–32 Zeichen (gleiche Regel wie der
// CHECK in Migration 0005). Dient auch als Pfadsegment `/t/{subdomain}`
// (Entscheidung 35).
export const subdomainSchema = z
  .string()
  .regex(/^[a-z0-9][a-z0-9-]{1,30}[a-z0-9]$/, "Ungültige Subdomain");

// Einladungs-Token: 32 Zufallsbytes, base64url-kodiert = 43 Zeichen.
export const invitationTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/, "Ungültiges Token");

// Mandantenstatus, der für Benutzer sichtbar ist (suspendierte Mandanten
// liefert die API gar nicht erst aus).
export const tenantStatusSchema = z.enum(["trial", "active"]);

// --- GET /api/me ---------------------------------------------------------------

export const membershipSchema = z.object({
  tenantId: uuidSchema,
  name: z.string(),
  subdomain: subdomainSchema,
  status: tenantStatusSchema,
  role: systemRoleSchema,
});
export type Membership = z.infer<typeof membershipSchema>;

export const meResponseSchema = z.object({
  userId: uuidSchema,
  activeTenantId: uuidSchema.nullable(),
  memberships: z.array(membershipSchema),
});
export type MeResponse = z.infer<typeof meResponseSchema>;

// --- POST /api/onboarding/trial ------------------------------------------------

export const createTrialRequestSchema = z.object({
  name: z.string().trim().min(2).max(100),
  subdomain: subdomainSchema,
});
export type CreateTrialRequest = z.infer<typeof createTrialRequestSchema>;

export const createTrialResponseSchema = z.object({ tenantId: uuidSchema });

// --- POST /api/invitations/accept ---------------------------------------------

export const acceptInvitationRequestSchema = z.object({ token: invitationTokenSchema });

export const acceptInvitationResponseSchema = z.object({
  tenantId: uuidSchema,
  role: systemRoleSchema,
  alreadyMember: z.boolean(),
});

// --- GET /api/tenant/members ---------------------------------------------------

export const memberSchema = z.object({
  userId: uuidSchema,
  email: z.string(),
  name: z.string().nullable(),
  role: systemRoleSchema,
  // JSON kennt kein Datum: kommt als ISO-String an.
  joinedAt: z.string(),
});
export type Member = z.infer<typeof memberSchema>;

export const membersResponseSchema = z.object({ members: z.array(memberSchema) });

// --- POST /api/tenant/invitations ---------------------------------------------

export const createInvitationRequestSchema = z.object({
  role: systemRoleSchema,
  emailHint: z.email().max(320).optional(),
});
export type CreateInvitationRequest = z.infer<typeof createInvitationRequestSchema>;

// Das Klartext-Token kommt genau einmal in dieser Antwort. Den Link baut der
// Client als `{origin}/invite#{token}`: der Teil nach "#" wird nie an einen
// Server geschickt und landet so in keinem Zugriffslog.
export const createInvitationResponseSchema = z.object({
  invitationId: uuidSchema,
  token: invitationTokenSchema,
  expiresAt: z.string(),
});

// --- Fehlerformat (alle Endpunkte) ---------------------------------------------
// Entspricht handleError in apps/api/src/errors.ts.

export const apiErrorBodySchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    requestId: z.string().optional(),
    issues: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
  }),
});
export type ApiErrorBody = z.infer<typeof apiErrorBodySchema>;
